import events from "../events/events";
import EventTarget from "../events/EventTarget";
/**
 * @fileOverview A class that's responsible for assisting with handling
 * dragging on an element. It dispatches events when the user is dragging.
 */

var DragHandler = function() {
  EventTarget.call(this);
  this.elements_ = [];

  this.bindedOnPointerDown_ = this.onPointerDown_.bind(this);
  this.bindedOnPointerUp_ = this.onPointerUp_.bind(this);
  this.bindedOnPointerMove_ = this.onPointerMove_.bind(this);
  this.bindedOnPointerLeave_ = this.onPointerLeave_.bind(this);

  this.currentX_ = 0;
  this.currentY_ = 0;
};
DragHandler.prototype = new EventTarget();

DragHandler.prototype.attachTo = function(element) {
  var matchingElementIndex = this.elements_.findIndex((e) => e == element);
  // Check if its already attached
  if (matchingElementIndex != -1) return;
  this.elements_.push(element);
  events.listen(element, "pointerdown", this.bindedOnPointerDown_);

  this.addGlobalEventListeners_();
};

DragHandler.prototype.removeFrom = function(element) {
  var matchingElementIndex = this.elements_.findIndex((e) => e == element);
  if (matchingElementIndex == -1) return;

  this.elements_.splice(matchingElementIndex, 1);
  events.unlisten(element, "pointerdown", this.bindedOnPointerDown_);
};

DragHandler.prototype.addGlobalEventListeners_ = function() {
  if (this.globalEventListenersAdded_) return;
  this.globalEventListenersAdded_ = true;

  // Use passive: false to allow preventDefault on touch devices
  var options = { passive: false };
  document.body.addEventListener("pointerup", this.bindedOnPointerUp_, options);
  document.body.addEventListener("pointermove", this.bindedOnPointerMove_, options);
  document.body.addEventListener("pointerleave", this.bindedOnPointerLeave_, options);
  document.body.addEventListener("pointercancel", this.bindedOnPointerUp_, options);
};

DragHandler.prototype.onPointerDown_ = function(e) {
  // Only handle primary pointer (ignore multi-touch)
  if (!e.isPrimary) return;
  
  clearTimeout(this.dragStartDelay_);
  // Store the pointer ID and target element for capture
  this.activePointerId_ = e.pointerId;
  this.activePointerTarget_ = e.currentTarget;
  
  // This helps prevent a very slight drag if the user nudges the pointer from
  // triggering as a drag
  this.dragStartDelay_ = setTimeout(() => {
    this.dragging_ = true;
    // Capture the pointer to ensure we get all events even if pointer leaves element
    if (this.activePointerTarget_ && this.activePointerId_ !== undefined) {
      try {
        this.activePointerTarget_.setPointerCapture(this.activePointerId_);
      } catch (ex) {
        // Pointer capture not supported or failed
      }
    }
  }, 40);
  // Prevent default touch behavior (e.g., scrolling) when starting a drag
  if (e.pointerType === 'touch') {
    e.preventDefault();
  }
};

DragHandler.prototype.onPointerUp_ = function(_e) {
  // Release pointer capture if we had it
  if (this.activePointerTarget_ && this.activePointerId_ !== undefined) {
    try {
      this.activePointerTarget_.releasePointerCapture(this.activePointerId_);
    } catch (ex) {
      // Pointer capture release failed or not supported
    }
  }
  
  delete this.dragging_;
  delete this.startDragPos_;
  delete this.activePointerId_;
  delete this.activePointerTarget_;
  clearTimeout(this.dragStartDelay_);
  if (!this.dragChange_) return;
  this.currentX_ = this.currentX_ + this.dragChange_.clientX;
  this.currentY_ = this.currentY_ + this.dragChange_.clientY;
  delete this.startDragPos_;
  delete this.dragChange_;
  this.dispatchEvent({
    type: "stop",
    x: this.currentX_,
    y: this.currentY_,
  });
};

DragHandler.prototype.onPointerMove_ = function(e) {
  if (!this.dragging_) return;
  
  // Only handle the pointer that started the drag
  if (this.activePointerId_ !== undefined && e.pointerId !== this.activePointerId_) {
    return;
  }
  
  // Track Shift key state
  this.lastKnownShiftKey_ = e.shiftKey;
  
  // Prevent scrolling/zooming during drag on touch devices
  if (e.pointerType === 'touch') {
    e.preventDefault();
  }
  
  if (!this.startDragPos_) {
    this.startDragPos_ = {
      clientX: e.clientX,
      clientY: e.clientY,
    };
    this.dispatchEvent({
      type: "start",
      x: this.currentX_,
      y: this.currentY_,
      shiftKey: e.shiftKey,
    });
  }

  this.dragChange_ = {
    clientX: e.clientX - this.startDragPos_.clientX,
    clientY: e.clientY - this.startDragPos_.clientY,
  };

  this.dispatchMovement_();
};

DragHandler.prototype.onPointerLeave_ = function(e) {
  this.onPointerUp_(e);
};

DragHandler.prototype.dispatchMovement_ = function() {
  if (!this.dragChange_) {
    return this.dispatchEvent({
      type: "move",
      x: this.currentX_,
      y: this.currentY_,
      shiftKey: this.lastKnownShiftKey_ || false,
    });
  }
  this.dispatchEvent({
    type: "change",
    x: this.currentX_ + this.dragChange_.clientX,
    y: this.currentY_ + this.dragChange_.clientY,
    shiftKey: this.lastKnownShiftKey_ || false,
  });

  return this.dispatchEvent({
    type: "move",
    x: this.currentX_ + this.dragChange_.clientX,
    y: this.currentY_ + this.dragChange_.clientY,
    shiftKey: this.lastKnownShiftKey_ || false,
  });
};

DragHandler.prototype.destroy = function() {
  for (var element of this.elements_) {
    events.unlisten(element, "pointerdown", this.bindedOnPointerDown_);
  }
  var options = { passive: false };
  document.body.removeEventListener("pointerup", this.bindedOnPointerUp_, options);
  document.body.removeEventListener("pointermove", this.bindedOnPointerMove_, options);
  document.body.removeEventListener("pointerleave", this.bindedOnPointerLeave_, options);
  document.body.removeEventListener("pointercancel", this.bindedOnPointerUp_, options);
  EventTarget.prototype.destroy.call(this);
};

/**
 * Resets the x and y offsets to 0
 */
DragHandler.prototype.reset = function() {
  this.currentX_ = 0;
  this.currentY_ = 0;
};

/**
 * Set the current x and y position.
 */
DragHandler.prototype.setPosition = function(coord) {
  this.currentX_ = coord.x;
  this.currentY_ = coord.y;
};

DragHandler.prototype.getPosition = function() {
  return {
    x: this.currentX_,
    y: this.currentY_,
  };
};

export default DragHandler;
